import React, { useState, useEffect } from 'react';
import { ProgressBar } from 'primereact/progressbar';
import { Button } from 'primereact/button';

interface BackgroundProcess {
  id: string;
  title: string;
  description?: string;
  progress?: number;
  status: 'running' | 'completed' | 'failed' | 'paused';
  startTime: number;
  endTime?: number;
  canCancel?: boolean;
  canPause?: boolean;
  onCancel?: () => void;
  onPause?: () => void;
  onResume?: () => void;
}

interface BackgroundProcessManagerState {
  processes: Map<string, BackgroundProcess>;
  listeners: ((processes: BackgroundProcess[]) => void)[];
}

class BackgroundProcessManager {
  private static instance: BackgroundProcessManager;
  private state: BackgroundProcessManagerState = {
    processes: new Map(),
    listeners: []
  };

  static getInstance(): BackgroundProcessManager {
    if (!BackgroundProcessManager.instance) {
      BackgroundProcessManager.instance = new BackgroundProcessManager();
    }
    return BackgroundProcessManager.instance;
  }

  addProcess(process: BackgroundProcess): void {
    this.state.processes.set(process.id, process);
    this.notifyListeners();
  }

  updateProcess(id: string, updates: Partial<BackgroundProcess>): void {
    const process = this.state.processes.get(id);
    if (process) {
      const updatedProcess = { ...process, ...updates };
      this.state.processes.set(id, updatedProcess);
      this.notifyListeners();
    }
  }

  removeProcess(id: string): void {
    this.state.processes.delete(id);
    this.notifyListeners();
  }

  getProcess(id: string): BackgroundProcess | undefined {
    return this.state.processes.get(id);
  }

  getAllProcesses(): BackgroundProcess[] {
    return Array.from(this.state.processes.values());
  }

  getActiveProcesses(): BackgroundProcess[] {
    return this.getAllProcesses().filter(p => p.status === 'running' || p.status === 'paused');
  }

  subscribe(listener: (processes: BackgroundProcess[]) => void): () => void {
    this.state.listeners.push(listener);
    return () => {
      this.state.listeners = this.state.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners(): void {
    const processes = this.getAllProcesses();
    this.state.listeners.forEach(listener => listener(processes));
  }

  // Auto-cleanup completed processes after 30 seconds
  private autoCleanup(): void {
    const now = Date.now();
    const processesToRemove: string[] = [];

    this.state.processes.forEach((process, id) => {
      if (process.status === 'completed' || process.status === 'failed') {
        const endTime = process.endTime || process.startTime;
        if (now - endTime > 30000) { // 30 seconds
          processesToRemove.push(id);
        }
      }
    });

    processesToRemove.forEach(id => this.removeProcess(id));
  }

  constructor() {
    // Run cleanup every 10 seconds
    setInterval(() => this.autoCleanup(), 10000);
  }
}

const processManager = BackgroundProcessManager.getInstance();

interface BackgroundProcessIndicatorProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  maxVisible?: number;
  showCompleted?: boolean;
}

const BackgroundProcessIndicator: React.FC<BackgroundProcessIndicatorProps> = ({
  position = 'top-right',
  maxVisible = 3,
  showCompleted = true
}) => {
  const [processes, setProcesses] = useState<BackgroundProcess[]>([]);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const unsubscribe = processManager.subscribe((allProcesses) => {
      let filteredProcesses = allProcesses;
      
      if (!showCompleted) {
        filteredProcesses = allProcesses.filter(p => p.status === 'running' || p.status === 'paused');
      }
      
      // Sort by start time (newest first)
      filteredProcesses.sort((a, b) => b.startTime - a.startTime);
      
      setProcesses(filteredProcesses.slice(0, maxVisible));
    });

    return unsubscribe;
  }, [maxVisible, showCompleted]);

  const getPositionStyles = () => {
    const baseStyles = {
      position: 'fixed' as const,
      zIndex: 9999,
      maxWidth: '400px',
      minWidth: '300px'
    };

    switch (position) {
      case 'top-right':
        return { ...baseStyles, top: '1rem', right: '1rem' };
      case 'top-left':
        return { ...baseStyles, top: '1rem', left: '1rem' };
      case 'bottom-right':
        return { ...baseStyles, bottom: '1rem', right: '1rem' };
      case 'bottom-left':
        return { ...baseStyles, bottom: '1rem', left: '1rem' };
      default:
        return { ...baseStyles, top: '1rem', right: '1rem' };
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return '#3b82f6';
      case 'completed':
        return '#22c55e';
      case 'failed':
        return '#ef4444';
      case 'paused':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return 'pi pi-spin pi-spinner';
      case 'completed':
        return 'pi pi-check-circle';
      case 'failed':
        return 'pi pi-times-circle';
      case 'paused':
        return 'pi pi-pause-circle';
      default:
        return 'pi pi-info-circle';
    }
  };

  const formatDuration = (startTime: number, endTime?: number) => {
    const duration = (endTime || Date.now()) - startTime;
    const seconds = Math.floor(duration / 1000);
    
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  if (processes.length === 0) {
    return null;
  }

  return (
    <div style={getPositionStyles()}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        border: '1px solid #e5e7eb',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#1f2937'
          }}>
            Background Processes ({processes.length})
          </div>
          <Button
            icon={collapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up'}
            text
            size="small"
            onClick={() => setCollapsed(!collapsed)}
          />
        </div>

        {/* Process List */}
        {!collapsed && (
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {processes.map((process) => (
              <div
                key={process.id}
                style={{
                  padding: '1rem',
                  borderBottom: '1px solid #f3f4f6'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem'
                }}>
                  <i
                    className={getStatusIcon(process.status)}
                    style={{
                      color: getStatusColor(process.status),
                      fontSize: '1rem',
                      marginTop: '0.125rem'
                    }}
                  />
                  
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#1f2937',
                      marginBottom: '0.25rem'
                    }}>
                      {process.title}
                    </div>
                    
                    {process.description && (
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        marginBottom: '0.5rem'
                      }}>
                        {process.description}
                      </div>
                    )}
                    
                    {typeof process.progress === 'number' && (
                      <div style={{ marginBottom: '0.5rem' }}>
                        <ProgressBar
                          value={process.progress}
                          style={{ height: '4px' }}
                          color={getStatusColor(process.status)}
                        />
                        <div style={{
                          fontSize: '0.625rem',
                          color: '#6b7280',
                          marginTop: '0.25rem'
                        }}>
                          {Math.round(process.progress)}% complete
                        </div>
                      </div>
                    )}
                    
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{
                        fontSize: '0.625rem',
                        color: '#6b7280'
                      }}>
                        {formatDuration(process.startTime, process.endTime)}
                      </div>
                      
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        {process.status === 'running' && process.canPause && process.onPause && (
                          <Button
                            icon="pi pi-pause"
                            size="small"
                            text
                            onClick={process.onPause}
                            tooltip="Pause"
                          />
                        )}
                        
                        {process.status === 'paused' && process.onResume && (
                          <Button
                            icon="pi pi-play"
                            size="small"
                            text
                            onClick={process.onResume}
                            tooltip="Resume"
                          />
                        )}
                        
                        {(process.status === 'running' || process.status === 'paused') && process.canCancel && process.onCancel && (
                          <Button
                            icon="pi pi-times"
                            size="small"
                            text
                            onClick={process.onCancel}
                            tooltip="Cancel"
                            severity="danger"
                          />
                        )}
                        
                        {(process.status === 'completed' || process.status === 'failed') && (
                          <Button
                            icon="pi pi-times"
                            size="small"
                            text
                            onClick={() => processManager.removeProcess(process.id)}
                            tooltip="Dismiss"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Hook for managing background processes
export const useBackgroundProcess = () => {
  const addProcess = (process: Omit<BackgroundProcess, 'startTime'>) => {
    const fullProcess: BackgroundProcess = {
      ...process,
      startTime: Date.now()
    };
    processManager.addProcess(fullProcess);
    return process.id;
  };

  const updateProcess = (id: string, updates: Partial<BackgroundProcess>) => {
    processManager.updateProcess(id, updates);
  };

  const removeProcess = (id: string) => {
    processManager.removeProcess(id);
  };

  const completeProcess = (id: string) => {
    processManager.updateProcess(id, {
      status: 'completed',
      endTime: Date.now(),
      progress: 100
    });
  };

  const failProcess = (id: string, error?: string) => {
    processManager.updateProcess(id, {
      status: 'failed',
      endTime: Date.now(),
      description: error
    });
  };

  return {
    addProcess,
    updateProcess,
    removeProcess,
    completeProcess,
    failProcess
  };
};

export { BackgroundProcessIndicator, processManager };
export default BackgroundProcessIndicator;